import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Link2, Mail, Lock, AlertCircle, Loader2, Eye, EyeOff, Globe, Package, ArrowDown } from 'lucide-react';
import { Footer } from '../components/layout/Footer';

export const LoginPage = () => {
  const { login, error: authError } = useAuth();
  const { language, setLanguage, t: globalT } = useLanguage();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const t = {
    es: {
      title: 'Iniciar Sesión',
      subtitle: 'Accede a tu cuenta para ver el catálogo',
      email: 'Email',
      password: 'Contraseña',
      login: 'Iniciar Sesión',
      noAccount: '¿No tienes cuenta?',
      register: 'Regístrate',
      forgotPassword: '¿Olvidaste tu contraseña?',
      invalidCredentials: 'Email o contraseña incorrectos',
      heroTitle: 'Marketplace de Enlaces Premium',
      heroSubtitle: 'Encuentra los mejores sitios web para tus campañas de link building. Filtros avanzados, métricas detalladas y la mejor selección de dominios.',
      statWebsites: 'Sitios Web',
      statCountries: 'Países Disponibles',
      statFilters: 'Filtros Profesionales',
      cta: 'Explorar Catálogo',
    },
    en: {
      title: 'Sign In',
      subtitle: 'Access your account to view the catalog',
      email: 'Email',
      password: 'Password',
      login: 'Sign In',
      noAccount: "Don't have an account?",
      register: 'Register',
      forgotPassword: 'Forgot your password?',
      invalidCredentials: 'Invalid email or password',
      heroTitle: 'Premium Link Marketplace',
      heroSubtitle: 'Find the best websites for your link building campaigns. Advanced filters, detailed metrics, and the best domain selection.',
      statWebsites: 'Websites',
      statCountries: 'Countries Available',
      statFilters: 'Professional Filters',
      cta: 'Explore Catalog',
    },
    de: {
      title: 'Anmelden',
      subtitle: 'Greifen Sie auf Ihr Konto zu',
      email: 'E-Mail',
      password: 'Passwort',
      login: 'Anmelden',
      noAccount: 'Noch kein Konto?',
      register: 'Registrieren',
      forgotPassword: 'Passwort vergessen?',
      invalidCredentials: 'Ungültige E-Mail oder Passwort',
      heroTitle: 'Premium Link Marktplatz',
      heroSubtitle: 'Finden Sie die besten Websites für Ihre Linkbuilding-Kampagnen.',
      statWebsites: 'Webseiten',
      statCountries: 'Länder',
      statFilters: 'Filter',
      cta: 'Katalog erkunden',
    },
    fr: {
      title: 'Connexion',
      subtitle: 'Accédez à votre compte',
      email: 'Email',
      password: 'Mot de passe',
      login: 'Se connecter',
      noAccount: "Pas de compte?",
      register: "S'inscrire",
      forgotPassword: 'Mot de passe oublié?',
      invalidCredentials: 'Email ou mot de passe invalide',
      heroTitle: 'Marketplace de Liens Premium',
      heroSubtitle: 'Trouvez les meilleurs sites web pour vos campagnes de link building.',
      statWebsites: 'Sites Web',
      statCountries: 'Pays',
      statFilters: 'Filtres',
      cta: 'Explorer le Catalogue',
    },
    it: {
      title: 'Accedi',
      subtitle: 'Accedi al tuo account',
      email: 'Email',
      password: 'Password',
      login: 'Accedi',
      noAccount: 'Non hai un account?',
      register: 'Registrati',
      forgotPassword: 'Password dimenticata?',
      invalidCredentials: 'Email o password non validi',
      heroTitle: 'Marketplace di Link Premium',
      heroSubtitle: 'Trova i migliori siti web per le tue campagne di link building.',
      statWebsites: 'Siti Web',
      statCountries: 'Paesi',
      statFilters: 'Filtri',
      cta: 'Esplora il Catalogo',
    },
    pt: {
      title: 'Entrar',
      subtitle: 'Acesse sua conta',
      email: 'Email',
      password: 'Senha',
      login: 'Entrar',
      noAccount: 'Não tem conta?',
      register: 'Registre-se',
      forgotPassword: 'Esqueceu sua senha?',
      invalidCredentials: 'Email ou senha inválidos',
      heroTitle: 'Marketplace de Links Premium',
      heroSubtitle: 'Encontre os melhores sites para suas campanhas de link building.',
      statWebsites: 'Sites',
      statCountries: 'Países',
      statFilters: 'Filtros',
      cta: 'Explorar Catálogo',
    },
    zh: {
      title: '登录',
      subtitle: '登录您的账户查看目录',
      email: '电子邮件',
      password: '密码',
      login: '登录',
      noAccount: '没有账户？',
      register: '注册',
      forgotPassword: '忘记密码？',
      invalidCredentials: '邮箱或密码错误',
      heroTitle: '高级链接市场',
      heroSubtitle: '为您的链接建设活动找到最佳网站。高级过滤器、详细指标和最佳域名选择。',
      statWebsites: '网站',
      statCountries: '可用国家',
      statFilters: '专业过滤器',
      cta: '浏览目录',
    },
    ar: {
      title: 'تسجيل الدخول',
      subtitle: 'قم بالوصول إلى حسابك لعرض الكتالوج',
      email: 'البريد الإلكتروني',
      password: 'كلمة المرور',
      login: 'تسجيل الدخول',
      noAccount: 'ليس لديك حساب؟',
      register: 'سجل الآن',
      forgotPassword: 'نسيت كلمة المرور؟',
      invalidCredentials: 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
      heroTitle: 'سوق الروابط المميزة',
      heroSubtitle: 'ابحث عن أفضل المواقع لحملات بناء الروابط الخاصة بك. فلاتر متقدمة ومقاييس مفصلة.',
      statWebsites: 'مواقع ويب',
      statCountries: 'دول متاحة',
      statFilters: 'فلاتر احترافية',
      cta: 'استكشف الكتالوج',
    },
  };

  const texts = t[language] || t.es;

  const languageOptions = [
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  ];

  const stats = [
    { icon: Package, value: '+30.000', label: texts.statWebsites },
    { icon: Globe, value: '+16', label: texts.statCountries },
    { icon: Globe, value: '+10', label: texts.statFilters },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error || texts.invalidCredentials);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Hero Section - Same as Homepage */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-hero-gradient" />
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Language Selector - Floating */}
        <div className="absolute top-4 right-4 z-10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="sm" className="gap-2 bg-white/90 hover:bg-white text-foreground shadow-lg">
                <Globe className="h-4 w-4" />
                <span className="uppercase font-medium">{language}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {languageOptions.map((lang) => (
                <DropdownMenuItem
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={language === lang.code ? 'bg-primary/10 text-primary' : ''}
                >
                  <span className="mr-2">{lang.flag}</span>
                  {lang.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Content */}
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Text Content */}
            <div className="text-center lg:text-left">
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground leading-tight mb-6 animate-fade-in-up">
                {texts.heroTitle}
              </h1>
              <p className="text-lg sm:text-xl text-primary-foreground/80 max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                {texts.heroSubtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                <Button
                  asChild
                  size="lg"
                  className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 font-semibold px-8 shadow-lg"
                >
                  <a href="#login">
                    {texts.cta}
                    <ArrowDown className="ml-2 h-5 w-5" />
                  </a>
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 sm:gap-8 mt-12 pt-8 border-t border-primary-foreground/20 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                {stats.map((stat, index) => (
                  <div key={index} className="text-center lg:text-left">
                    <div className="flex items-center justify-center lg:justify-start gap-2 mb-1">
                      <stat.icon className="h-5 w-5 text-primary-foreground/70" />
                      <span className="text-2xl sm:text-3xl font-bold text-primary-foreground">
                        {stat.value}
                      </span>
                    </div>
                    <span className="text-sm text-primary-foreground/70">{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative hidden lg:block animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop&q=80"
                  alt="SEO Analytics Dashboard"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/40 to-transparent" />
              </div>
              
              {/* Floating Cards */}
              <div className="absolute -bottom-6 -left-6 bg-card rounded-xl shadow-xl p-4 animate-bounce-soft">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-success/10 flex items-center justify-center">
                    <span className="text-success font-bold">DR</span>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Average</p>
                    <p className="font-bold text-foreground">72+</p>
                  </div>
                </div>
              </div>

              <div className="absolute -top-4 -right-4 bg-card rounded-xl shadow-xl p-4 animate-bounce-soft" style={{ animationDelay: '0.5s' }}>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-accent/10 flex items-center justify-center">
                    <span className="text-accent font-bold">€</span>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">From</p>
                    <p className="font-bold text-foreground">€45</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave decoration */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            viewBox="0 0 1440 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-auto"
          >
            <path
              d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V120Z"
              className="fill-background"
            />
          </svg>
        </div>
      </section>

      {/* Login Section */}
      <section id="login" className="py-16 lg:py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-md mx-auto">
            {/* Centered Logo */}
            <div className="flex flex-col items-center mb-8 animate-fade-in-up">
              <img 
                src="/logo_purolink.webp" 
                alt="Purolink" 
                className="h-20 w-auto mb-4"
              />
            </div>

            {/* Login Card */}
            <Card className="shadow-2xl border-border/60 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-2xl font-display">{texts.title}</CardTitle>
                <CardDescription>{texts.subtitle}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {(error || authError) && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error || authError}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email">{texts.email}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="email@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">{texts.password}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {texts.login}...
                      </>
                    ) : (
                      texts.login
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center text-sm space-y-2">
                  <div>
                    <Link to="/forgot-password" className="text-primary hover:underline font-medium">
                      {texts.forgotPassword}
                    </Link>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{texts.noAccount} </span>
                    <Link to="/register" className="text-primary hover:underline font-medium">
                      {texts.register}
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LoginPage;
